import { auth } from './firebase'

const API_BASE = import.meta.env.VITE_API_BASE_URL || ''

interface RequestOptions extends RequestInit {
  params?: Record<string, string>
}

async function getAuthToken(): Promise<string | null> {
  const user = auth.currentUser
  if (!user) return null
  return user.getIdToken()
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { params, ...init } = options

  let url = `${API_BASE}${path}`
  if (params) {
    const searchParams = new URLSearchParams(params)
    url += `?${searchParams.toString()}`
  }

  const token = await getAuthToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(url, {
    ...init,
    headers,
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(error || `Request failed: ${response.status}`)
  }

  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

async function requestBlob(path: string): Promise<string> {
  const url = `${API_BASE}${path}`

  const token = await getAuthToken()
  const headers: Record<string, string> = {}
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(url, { headers })

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`)
  }

  const blob = await response.blob()
  return URL.createObjectURL(blob)
}

// --- Types ---

export type PartyStatus = 'waiting' | 'active' | 'paused' | 'stopped' | 'ended'

export interface PartyListItem {
  id: string
  code: string
  name: string
  admin_uid: string
  admin_email: string
  status: PartyStatus
  max_participants: number
  created_at: string
  started_at: string | null
  ended_at: string | null
  updated_at: string
  participant_count: number
}

export interface PartyList {
  parties: PartyListItem[]
  total: number
  limit: number
  offset: number
}

export interface Participant {
  id: string
  party_id: string
  user_uid: string
  user_email: string
  device_name: string
  role: 'admin' | 'participant'
  status: 'connected' | 'streaming' | 'paused' | 'disconnected'
  joined_at: string
  left_at: string | null
}

export interface PartyDetail {
  id: string
  code: string
  name: string
  admin_uid: string
  admin_email: string
  status: PartyStatus
  max_participants: number
  created_at: string
  started_at: string | null
  ended_at: string | null
  updated_at: string
  participants: Participant[]
}

export interface CreatePartyRequest {
  name: string
  max_participants?: number
}

export interface JoinPartyResponse {
  party_id: string
  name: string
  role: string
  livekit: {
    url: string
    token: string
  }
}

export type CommandType = 'start' | 'pause' | 'resume' | 'stop' | 'kick' | 'mute' | 'unmute'

export interface CommandResponse {
  type: string
  status: string
  command_id?: string
}

// --- API Methods ---

export const partyApi = {
  health: () => request<{ status: string }>('/healthz'),

  list: (params?: Record<string, string>) =>
    request<PartyList>('/api/parties', { params }),

  create: (data: CreatePartyRequest) =>
    request<PartyDetail>('/api/parties', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  get: (id: string) =>
    request<PartyDetail>(`/api/parties/${id}`),

  end: (id: string) =>
    request<{ message: string; party_id: string }>(`/api/parties/${id}/end`, {
      method: 'POST',
    }),

  getQR: (id: string, size?: number) => {
    const path = size
      ? `/api/parties/${id}/qr?size=${size}`
      : `/api/parties/${id}/qr`
    return requestBlob(path)
  },

  join: (code: string, deviceName: string) =>
    request<JoinPartyResponse>('/api/parties/join', {
      method: 'POST',
      body: JSON.stringify({ code, device_name: deviceName }),
    }),

  sendCommand: (id: string, type: CommandType, targetUid?: string) =>
    request<CommandResponse>(`/api/parties/${id}/commands`, {
      method: 'POST',
      body: JSON.stringify({ type, ...(targetUid ? { target_uid: targetUid } : {}) }),
    }),
}
